import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { AuthState, AuthUser } from "../../shared/types";
import { api } from "../api";

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  needsBootstrap: boolean;
  canWrite: boolean;
  serverDate: string;
  timezone: "Asia/Shanghai";
  serverToday: () => string;
  login: (username: string, password: string) => Promise<void>;
  bootstrap: (username: string, displayName: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [serverDate, setServerDate] = useState("");
  const clock = useRef({ epoch: 0, observedAt: 0 });

  const applyServerClock = useCallback((state: Pick<AuthState, "serverNow" | "serverDate">) => {
    clock.current = { epoch: Date.parse(state.serverNow), observedAt: performance.now() };
    setServerDate(state.serverDate);
  }, []);

  const serverToday = useCallback(() => {
    if (!clock.current.epoch) return serverDate;
    const current = new Date(clock.current.epoch + performance.now() - clock.current.observedAt);
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(current);
    const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
    return `${value("year")}-${value("month")}-${value("day")}`;
  }, [serverDate]);

  const refresh = useCallback(async () => {
    try {
      const state = await api.authSession();
      setUser(state.user);
      setNeedsBootstrap(state.needsBootstrap);
      applyServerClock(state);
    } catch { setUser(null); }
    finally { setLoading(false); }
  }, [applyServerClock]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    const expired = () => { setUser(null); setLoading(false); };
    window.addEventListener("auth-expired", expired);
    return () => window.removeEventListener("auth-expired", expired);
  }, []);

  async function login(username: string, password: string) {
    const result = await api.login({ username, password });
    setUser(result.user); setNeedsBootstrap(false); applyServerClock(result);
  }
  async function bootstrap(username: string, displayName: string, password: string) {
    const result = await api.bootstrap({ username, displayName, password });
    setUser(result.user); setNeedsBootstrap(false); applyServerClock(result);
  }
  async function logout() {
    try { await api.logout(); } finally { setUser(null); }
  }

  return <AuthContext.Provider value={{ user, loading, needsBootstrap, canWrite: user?.role === "admin", serverDate, timezone: "Asia/Shanghai", serverToday, login, bootstrap, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth 必须在 AuthProvider 中使用");
  return context;
}
